// PATH: src/pages/PersonnelPage.tsx — Phase 4 : annuaire complet, fonctions
// locales, lien compte, congés/remplacements, charge horaire, formations.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Button, Card, Col, DatePicker, Divider, Empty, Form, Input, InputNumber, List, Modal,
  Popconfirm, Row, Select, Space, Tabs, Tag, Typography, message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useApiClient } from "../api/apiClients";
import { usePedagogyApi, type Staff } from "../api/pedagogy";
import { useIdentityApi } from "../api/identity";
import { useTabParam } from "../hooks/useTabParam";
import ResponsiveTable from "../components/ResponsiveTable";

const ROLES = ["DIRECTION", "COMPTABLE", "SURVEILLANT_GENERAL", "ENSEIGNANT", "AUTRE"];

// Seul un enseignant porte des matières. Les autres fonctions (comptable,
// direction…) se voyaient proposer la liste, et les liens créés par erreur
// ressortaient ensuite dans l'annuaire et dans la charge horaire.
const TEACHING_ROLE = "ENSEIGNANT";
const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée");

/**
 * StaffCreateModal — création d'un membre du personnel.
 *
 * Un ENSEIGNANT reçoit un compte central tc_identity, par le même parcours que
 * tc_tilmicode_ui (UserForm) et que l'inscription d'un élève : identifiant
 * généré par tc_identity, puis création déléguée. Les autres fonctions
 * (direction, comptable…) restent des fiches purement locales.
 *
 * C'est cet écran qui décide d'envoyer le volet `account` ou non — le backend
 * n'inspecte pas le rôle, exactement comme pour un élève.
 */
const StaffCreateModal: React.FC<{
  open: boolean;
  subjects: any[];
  onCancel: () => void;
  onCreated: () => void;
}> = ({ open, subjects, onCancel, onCreated }) => {
  const api = usePedagogyApi();
  const identityApi = useIdentityApi();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [generatingLogin, setGeneratingLogin] = useState(false);

  // Form.useWatch : le volet compte apparaît/disparaît au changement de fonction.
  const role = Form.useWatch("role", form);
  const isTeacher = role === TEACHING_ROLE;

  const generateLogin = async () => {
    try {
      const v = await form.validateFields(["first_name", "last_name"]);
      setGeneratingLogin(true);
      form.setFieldValue(["account", "login"], await identityApi.generateLogin(v.first_name, v.last_name));
    } catch (err: any) {
      if (err?.errorFields) return; // erreurs de validation : déjà sous les champs
      message.error("Génération de l'identifiant impossible");
    } finally {
      setGeneratingLogin(false);
    }
  };

  const onFinish = async (values: any) => {
    if (saving) return;
    setSaving(true);
    try {
      const { account, ...staff } = values;
      // Le champ est masqué hors enseignant, mais un changement de fonction en
      // cours de saisie laisse les matières déjà cochées dans le formulaire.
      let payload: any = { ...staff, subject_ids: isTeacher ? staff.subject_ids : [] };

      if (isTeacher) {
        // Identifiant manquant (bouton non cliqué) : le générer avant l'envoi
        // plutôt que d'échouer côté backend.
        const login = account?.login || (await identityApi.generateLogin(values.first_name, values.last_name));
        payload = { ...payload, account: { ...account, login } };
      }

      const res = await api.createStaff(payload);
      // Un compte TEACHER est créé INACTIF : sans cet email, la personne ne
      // pourra jamais activer son compte. tc_identity n'échoue pas pour autant.
      if (isTeacher && res.email_sent === false) {
        message.warning("Membre créé, mais l'email d'activation n'a pas pu être envoyé. Contactez le support.");
      } else {
        message.success(isTeacher ? "Enseignant créé — email d'activation envoyé" : "Membre créé");
      }
      form.resetFields();
      onCreated();
    } catch (err: any) {
      message.error(err?.response?.data?.error || "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouveau membre du personnel" open={open} onCancel={onCancel} footer={null} width={620} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ role: "ENSEIGNANT" }}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="last_name" label="Nom" rules={[{ required: true, message: "Nom requis" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="first_name" label="Prénom" rules={[{ required: true, message: "Prénom requis" }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="role" label="Fonction">
              <Select options={ROLES.map((x) => ({ value: x, label: x }))} />
            </Form.Item>
          </Col>
          {isTeacher && (
            <Col xs={24} md={12}>
              <Form.Item name="subject_ids" label="Matières enseignées">
                <Select mode="multiple" allowClear placeholder="Aucune matière"
                  options={subjects.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))} />
              </Form.Item>
            </Col>
          )}
        </Row>

        {isTeacher && (
          <>
            <Divider orientation="left">Compte Tilmid</Divider>
            <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
              L'enseignant reçoit un compte de l'écosystème, comme ceux créés depuis Tilmicode. Le
              compte est créé <strong>inactif</strong> : un email d'activation lui est envoyé, d'où
              l'adresse obligatoire.
            </Typography.Paragraph>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item label="Identifiant" required>
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item name={["account", "login"]} noStyle
                      rules={[{ required: true, message: "Générez l'identifiant" }]}>
                      <Input disabled placeholder="Généré depuis le nom et le prénom" />
                    </Form.Item>
                    <Button type="primary" onClick={generateLogin} loading={generatingLogin}>Générer</Button>
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                {/* tc_identity REFUSE un compte TEACHER sans email : le champ
                    est obligatoire, pas optionnel comme pour un élève. */}
                <Form.Item name={["account", "email"]} label="Email"
                  rules={[
                    { required: true, message: "Email requis pour un enseignant" },
                    { type: "email", message: "Email invalide" },
                  ]}>
                  <Input placeholder="nom@example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name={["account", "password"]} label="Mot de passe (facultatif)">
                  <Input.Password />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Button type="primary" htmlType="submit" loading={saving}>Créer</Button>
      </Form>
    </Modal>
  );
};

// StaffEditModal — état civil et matières enseignées d'un membre du personnel.
// `staff` non nul ouvre la modale ; destroyOnClose remonte le formulaire à
// chaque ouverture pour que initialValues suive le membre sélectionné.
const StaffEditModal: React.FC<{
  staff: Staff | null;
  subjects: any[];
  onCancel: () => void;
  onSubmit: (values: { last_name: string; first_name: string; subject_ids?: number[] }) => void;
}> = ({ staff, subjects, onCancel, onSubmit }) => (
  <Modal
    title={staff ? `Modifier ${staff.last_name} ${staff.first_name}` : "Modifier"}
    open={!!staff}
    onCancel={onCancel}
    footer={null}
    destroyOnClose
  >
    <Form
      layout="vertical"
      onFinish={onSubmit}
      initialValues={{
        last_name: staff?.last_name,
        first_name: staff?.first_name,
        subject_ids: staff?.subject_ids ?? [],
      }}
    >
      {/* Nom et prénom d'un membre RATTACHÉ à un compte central ne sont pas
          modifiables ici : SyncStaff les réécrit depuis personalinfo à chaque
          chargement de l'annuaire (« le central gagne »). Les proposer donnerait
          un formulaire qui paraît enregistrer, puis revient en arrière. */}
      {staff?.userapp_id ? (
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={`Nom et prénom pilotés par le compte Tilmid #${staff.userapp_id}`}
          description="Ils se modifient depuis le compte central ; les matières restent gérées ici."
        />
      ) : (
        <>
          <Form.Item name="last_name" label="Nom" rules={[{ required: true, message: "Nom requis" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="first_name" label="Prénom" rules={[{ required: true, message: "Prénom requis" }]}>
            <Input />
          </Form.Item>
        </>
      )}
      {/* La fonction ne se modifie pas ici : celle de la fiche décide. */}
      {staff?.role === TEACHING_ROLE && (
        <Form.Item name="subject_ids" label="Matières enseignées">
          <Select
            mode="multiple"
            allowClear
            placeholder="Aucune matière"
            options={subjects.map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          />
        </Form.Item>
      )}
      <Button type="primary" htmlType="submit">Enregistrer</Button>
    </Form>
  </Modal>
);

const PersonnelPage: React.FC = () => {
  const http = useApiClient("tilmischool");
  const api = usePedagogyApi();
  const [tab, setTab] = useTabParam("annuaire");
  const [staff, setStaff] = useState<Staff[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [workload, setWorkload] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [trainings, setTrainings] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [linking, setLinking] = useState<Staff | null>(null);
  const [creating, setCreating] = useState(false);
  // Membre en cours d'édition : porte la fiche COMPLÈTE, dont les champs
  // absents du formulaire (téléphone, CIN, adresse…) qu'il faut renvoyer tels
  // quels — le backend remplace la fiche entière.
  const [editing, setEditing] = useState<Staff | null>(null);
  const [orgAccounts, setOrgAccounts] = useState<{ id: number; login: string; email?: string }[]>([]);

  const load = useCallback(() => {
    api.listStaff().then(setStaff).catch(() => undefined);
    // Matières : nécessaires à l'affectation des enseignants, rapatriée ici
    // depuis « Admin pédagogique » — les deux écrans éditaient school_staff.
    api.listSubjects().then(setSubjects).catch(() => undefined);
    http.get("/api/v1/school/staff/workload").then((r) => setWorkload(r.data ?? [])).catch(() => undefined);
    http.get("/api/v1/school/leaves").then((r) => setLeaves(r.data ?? [])).catch(() => setLeaves([]));
    http.get("/api/v1/school/trainings").then((r) => setTrainings(r.data ?? [])).catch(() => setTrainings([]));
    http.get("/api/v1/school/staff-reviews").then((r) => setReviews(r.data ?? [])).catch(() => setReviews([]));
    http.get("/api/v1/school/org-accounts").then((r) => setOrgAccounts(r.data ?? [])).catch(() => setOrgAccounts([]));
  }, [api, http]);
  useEffect(load, [load]);

  const active = staff.filter((s) => s.status === "ACTIF");
  // Les archivés restent listés (en fin de tableau) : sans cela, un doublon
  // archivé — invisible ici mais toujours pris en compte par l'import d'emploi
  // du temps — ne pourrait jamais être supprimé.
  const archived = staff.filter((s) => s.status !== "ACTIF");
  const ordered = [...active, ...archived];
  const hours = (id: number) => workload.find((w) => w.staff_id === id);
  const subjectCodes = (ids?: number[]) =>
    (ids ?? []).map((id) => subjects.find((x) => x.id === id)?.code).filter(Boolean);

  return (
    <div>
      <Typography.Title level={3}>Personnel</Typography.Title>
      <Tabs activeKey={tab} onChange={setTab} items={[
        {
          key: "annuaire", label: "Annuaire & charge horaire",
          children: (
            <Card
              // Création rapatriée depuis « Admin pédagogique » : le personnel
              // se crée là où il se gère, et non dans un écran de réglages.
              extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
                  Ajouter un membre
                </Button>
              }
            >
              <StaffCreateModal
                open={creating}
                subjects={subjects}
                onCancel={() => setCreating(false)}
                onCreated={() => { setCreating(false); load(); }}
              />

              {/* sticky : l'annuaire dépasse vite l'écran, et sans en-tête
                  figée on ne sait plus quelle colonne on lit. */}
              <ResponsiveTable<Staff> rowKey="id" dataSource={ordered} pagination={false} size="small"
                scroll={{ x: 1200 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun membre du personnel" /> }}
                // Sur téléphone, chaque membre devient une fiche. La fonction
                // reste modifiable (c'est l'action la plus fréquente ici), les
                // colonnes de contexte deviennent une ligne de détail.
                card={{
                  title: (s) => (
                    <Space size={8}>
                      <span>{s.last_name} {s.first_name}</span>
                      {s.status !== "ACTIF" && <Tag>Archivé</Tag>}
                    </Space>
                  ),
                  extra: (s) => <Tag bordered={false}>{s.role}</Tag>,
                  description: (s) => {
                    const w = hours(s.id);
                    const codes = subjectCodes(s.subject_ids);
                    return (
                      <>
                        <Select size="small" value={s.role} style={{ width: 180 }}
                          options={ROLES.map((x) => ({ value: x, label: x }))}
                          onChange={(v) => http.put(`/api/v1/school/staff/${s.id}/role`, { role: v }).then(load).catch(apiError)} />
                        {codes.map((c) => <Tag key={c} bordered={false}>{c}</Tag>)}
                        {s.userapp_id
                          ? <Tag color="green">Compte #{s.userapp_id}</Tag>
                          : <Button size="small" onClick={() => setLinking(s)}>Lier un compte</Button>}
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {w ? `${w.weekly_hours.toFixed(1)} h/sem · ${w.slot_count} créneaux` : "Aucun créneau"}
                          {s.hire_date ? ` · embauché le ${dayjs(s.hire_date).format("DD/MM/YYYY")}` : ""}
                        </Typography.Text>
                      </>
                    );
                  },
                  actions: (s) => [
                    <Button key="edit" size="small" type="text" onClick={() => setEditing(s)}>Modifier</Button>,
                    s.status === "ACTIF" && (
                      <Popconfirm key="arch" title="Archiver ce membre ?" okText="Archiver" cancelText="Annuler"
                        onConfirm={() => api.archiveStaff(s.id).then(() => { message.success("Membre archivé"); load(); }).catch(apiError)}>
                        <Button size="small" type="text" danger>Archiver</Button>
                      </Popconfirm>
                    ),
                    <Popconfirm key="del"
                      title="Supprimer définitivement ce membre ?"
                      description="Action irréversible. Refusée s'il a des séances, des évaluations, ou un compte Tilmid rattaché."
                      okText="Supprimer" okButtonProps={{ danger: true }} cancelText="Annuler"
                      onConfirm={() => api.deleteStaff(s.id).then(() => { message.success("Membre supprimé"); load(); }).catch(apiError)}>
                      <Button size="small" type="text" danger>Supprimer</Button>
                    </Popconfirm>,
                  ],
                }}
                columns={[
                  { title: "Nom", render: (_, s) => (
                    <Space size={8}>
                      <span style={{ fontWeight: 500 }}>{s.last_name} {s.first_name}</span>
                      {s.status !== "ACTIF" && <Tag>Archivé</Tag>}
                    </Space>) },
                  { title: "Fonction", dataIndex: "role", width: 210, render: (r: string, s) => (
                    <Select size="small" value={r} style={{ width: 190 }}
                      options={ROLES.map((x) => ({ value: x, label: x }))}
                      onChange={(v) => http.put(`/api/v1/school/staff/${s.id}/role`, { role: v }).then(load).catch(apiError)} />) },
                  { title: "Matières", render: (_, s) => {
                    const codes = subjectCodes(s.subject_ids);
                    return codes.length
                      ? <Space size={4} wrap>{codes.map((c) => <Tag key={c} bordered={false}>{c}</Tag>)}</Space>
                      : <Typography.Text type="secondary">aucune</Typography.Text>;
                  } },
                  { title: "Compte lié", width: 170, render: (_, s) => s.userapp_id
                      ? <Space><Tag color="green">#{s.userapp_id}</Tag>
                          <Button size="small" type="text" danger
                            onClick={() => http.put(`/api/v1/school/staff/${s.id}/account`, { userapp_id: null }).then(load).catch(apiError)}>
                            Délier</Button></Space>
                      : <Button size="small" onClick={() => setLinking(s)}>Lier un compte</Button> },
                  { title: "Charge / semaine", width: 160, render: (_, s) => {
                    const w = hours(s.id);
                    return w ? `${w.weekly_hours.toFixed(1)} h (${w.slot_count} créneaux)` : "—";
                  } },
                  { title: "Embauche", dataIndex: "hire_date", width: 110, render: (v?: string) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
                  { title: "", width: 270, fixed: "right", align: "right", render: (_, s) => (
                    <Space>
                      <Button size="small" type="text" onClick={() => setEditing(s)}>Modifier</Button>
                      {/* Archiver : masque le membre du quotidien sans rien perdre.
                          Réservé aux actifs — archiver un archivé n'a pas de sens. */}
                      {s.status === "ACTIF" && (
                        <Popconfirm title="Archiver ce membre ?" okText="Archiver" cancelText="Annuler"
                          onConfirm={() => api.archiveStaff(s.id).then(() => { message.success("Membre archivé"); load(); }).catch(apiError)}>
                          <Button size="small" type="text" danger>Archiver</Button>
                        </Popconfirm>
                      )}
                      {/* Supprimer : DÉFINITIF. Le backend refuse si des séances,
                          des évaluations ou un compte central le référencent — ce
                          dernier serait rematérialisé par SyncStaff (message renvoyé
                          par apiError). */}
                      <Popconfirm
                        title="Supprimer définitivement ce membre ?"
                        description="Action irréversible. Refusée s'il a des séances, des évaluations, ou un compte Tilmid rattaché."
                        okText="Supprimer" okButtonProps={{ danger: true }} cancelText="Annuler"
                        onConfirm={() => api.deleteStaff(s.id).then(() => { message.success("Membre supprimé"); load(); }).catch(apiError)}>
                        <Button size="small" type="text" danger>Supprimer</Button>
                      </Popconfirm>
                    </Space>) },
                ]} />


              <StaffEditModal
                staff={editing}
                subjects={subjects}
                onCancel={() => setEditing(null)}
                onSubmit={(values) =>
                  // Fiche existante d'abord : seuls les champs du formulaire sont
                  // remplacés, le reste est renvoyé inchangé. subject_ids est forcé à
                  // un tableau : vidé, le Select rend `undefined`, qu'axios omettrait
                  // du JSON — le backend ne verrait alors aucune matière à retirer.
                  api.updateStaff(editing!.id, {
                    ...editing!,
                    ...values,
                    subject_ids: values.subject_ids ?? [],
                  })
                    .then(() => {
                      setEditing(null);
                      message.success("Membre modifié");
                      load();
                    })
                    .catch(apiError)
                }
              />
            </Card>
          ),
        },
        {
          key: "conges", label: `Congés (${leaves.length})`,
          children: (
            <Card>
              <Form layout="inline" style={{ marginBottom: 12 }}
                onFinish={(v) => http.post("/api/v1/school/leaves", {
                  ...v,
                  start_date: dayjs(v.dates[0]).format("YYYY-MM-DD") + "T00:00:00Z",
                  end_date: dayjs(v.dates[1]).format("YYYY-MM-DD") + "T00:00:00Z",
                  dates: undefined,
                }).then(load).catch(apiError)}>
                <Form.Item name="staff_id" rules={[{ required: true, message: "Personnel requis" }]}>
                  <Select placeholder="Personnel" style={{ width: 180 }}
                    options={active.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
                </Form.Item>
                <Form.Item name="kind" initialValue="CONGE">
                  <Select style={{ width: 130 }} options={["CONGE", "ABSENCE", "MALADIE", "AUTRE"].map((k) => ({ value: k, label: k }))} />
                </Form.Item>
                <Form.Item name="dates" rules={[{ required: true, message: "Période requise" }]}><DatePicker.RangePicker format="DD/MM/YYYY" /></Form.Item>
                <Form.Item name="reason"><Input placeholder="Motif" /></Form.Item>
                <Button htmlType="submit" type="primary">Déclarer</Button>
              </Form>
              <ResponsiveTable<any> rowKey="id" dataSource={leaves} pagination={false} size="small"
                scroll={{ x: 1100 }}
                card={{
                  title: (l) => l.staff_name,
                  extra: (l) => (
                    <Tag color={l.status === "APPROUVE" ? "green" : l.status === "REFUSE" ? "red" : "orange"}>
                      {l.status}
                    </Tag>
                  ),
                  description: (l) => (
                    <>
                      <Tag bordered={false}>{l.kind}</Tag>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(l.start_date).format("DD/MM")} → {dayjs(l.end_date).format("DD/MM/YYYY")}
                        {l.replacement_name ? ` · remplacé par ${l.replacement_name}` : ""}
                      </Typography.Text>
                    </>
                  ),
                  actions: (l) =>
                    l.status === "EN_ATTENTE"
                      ? [
                          <ApproveLeave key="ap" leave={l} staff={active} onDone={load}
                            approve={(status, rep) =>
                              http.post(`/api/v1/school/leaves/${l.id}/status`, { status, replacement_staff_id: rep })
                                .then(load).catch(apiError)} />,
                        ]
                      : [],
                }}
                columns={[
                  { title: "Personnel", dataIndex: "staff_name" },
                  { title: "Type", dataIndex: "kind", width: 110, render: (k: string) => <Tag>{k}</Tag> },
                  { title: "Période", width: 170, render: (_, l: any) =>
                    `${dayjs(l.start_date).format("DD/MM")} → ${dayjs(l.end_date).format("DD/MM/YYYY")}` },
                  { title: "Statut", dataIndex: "status", width: 120, render: (s: string) =>
                    <Tag color={s === "APPROUVE" ? "green" : s === "REFUSE" ? "red" : "orange"}>{s}</Tag> },
                  { title: "Remplaçant", dataIndex: "replacement_name", render: (v?: string) => v || "—" },
                  { title: "", width: 350, fixed: "right", render: (_, l: any) => l.status === "EN_ATTENTE" && (
                    <ApproveLeave leave={l} staff={active} onDone={load}
                      approve={(status, rep) =>
                        http.post(`/api/v1/school/leaves/${l.id}/status`, { status, replacement_staff_id: rep })
                          .then(load).catch(apiError)} />) },
                ]} />
            </Card>
          ),
        },
        {
          key: "formations", label: "Formations & évaluations",
          children: (
            <Card>
              <Typography.Title level={5}>Formation continue</Typography.Title>
              <Form layout="inline" style={{ marginBottom: 12 }}
                onFinish={(v) => http.post("/api/v1/school/trainings", v).then(load).catch(apiError)}>
                <Form.Item name="staff_id" rules={[{ required: true, message: "Personnel requis" }]}>
                  <Select placeholder="Personnel" style={{ width: 180 }}
                    options={active.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
                </Form.Item>
                <Form.Item name="title" rules={[{ required: true, message: "Intitulé requis" }]}><Input placeholder="Intitulé" /></Form.Item>
                <Form.Item name="provider"><Input placeholder="Organisme" /></Form.Item>
                <Button htmlType="submit">Ajouter</Button>
              </Form>
              <List size="small" dataSource={trainings}
                renderItem={(t: any) => (
                  <List.Item actions={[
                    <Select key="st" size="small" value={t.status} style={{ width: 130 }}
                      options={["PLANIFIEE", "EN_COURS", "TERMINEE"].map((x) => ({ value: x, label: x }))}
                      onChange={(v) => http.post(`/api/v1/school/trainings/${t.id}/status`, { status: v }).then(load).catch(apiError)} />]}>
                    <List.Item.Meta title={`${t.title} — ${t.staff_name}`} description={t.provider || ""} />
                  </List.Item>)} />
              <Typography.Title level={5}>Évaluations du personnel</Typography.Title>
              <Form layout="inline" style={{ marginBottom: 12 }}
                onFinish={(v) => http.post("/api/v1/school/staff-reviews", {
                  ...v, review_date: dayjs(v.review_date).format("YYYY-MM-DD") + "T00:00:00Z",
                }).then(load).catch(apiError)}>
                <Form.Item name="staff_id" rules={[{ required: true, message: "Personnel requis" }]}>
                  <Select placeholder="Personnel" style={{ width: 180 }}
                    options={active.map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
                </Form.Item>
                <Form.Item name="review_date" rules={[{ required: true, message: "Date requise" }]}><DatePicker format="DD/MM/YYYY" /></Form.Item>
                <Form.Item name="rating"><InputNumber min={1} max={5} placeholder="Note /5" /></Form.Item>
                <Form.Item name="comments"><Input placeholder="Commentaires" style={{ width: 220 }} /></Form.Item>
                <Button htmlType="submit">Enregistrer</Button>
              </Form>
              <List size="small" dataSource={reviews}
                renderItem={(r: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${r.staff_name} — ${dayjs(r.review_date).format("DD/MM/YYYY")}${r.rating ? ` · ${r.rating}/5` : ""}`}
                      description={r.comments || ""} />
                  </List.Item>)} />
            </Card>
          ),
        },
      ]} />

      <Modal title={linking ? `Lier un compte à ${linking.last_name} ${linking.first_name}` : ""}
        open={!!linking} onCancel={() => setLinking(null)} footer={null} destroyOnClose>
        <Typography.Paragraph type="secondary">
          Sélectionnez le compte enseignant (tc_identity) de l'organisation — permet la vue enseignant et la saisie de notes.
        </Typography.Paragraph>
        <Form layout="inline"
          onFinish={(v) => http.put(`/api/v1/school/staff/${linking!.id}/account`, { userapp_id: v.userapp_id })
            .then(() => { setLinking(null); load(); }).catch(apiError)}>
          <Form.Item name="userapp_id" rules={[{ required: true, message: "Compte requis" }]}>
            <Select showSearch style={{ width: 280 }} placeholder="Rechercher un compte…"
              optionFilterProp="label"
              options={orgAccounts.map((a) => ({ value: a.id, label: `${a.login}${a.email ? " — " + a.email : ""}` }))}
              notFoundContent="Aucun compte enseignant trouvé" />
          </Form.Item>
          <Button htmlType="submit" type="primary">Lier</Button>
        </Form>
      </Modal>
    </div>
  );
};

const ApproveLeave: React.FC<{
  leave: any; staff: Staff[]; onDone: () => void;
  approve: (status: string, replacement?: number) => void;
}> = ({ staff, leave, approve }) => {
  const [rep, setRep] = useState<number>();
  return (
    <Space>
      <Select size="small" placeholder="Remplaçant" allowClear style={{ width: 160 }} value={rep} onChange={setRep}
        options={staff.filter((s) => s.id !== leave.staff_id).map((s) => ({ value: s.id, label: `${s.last_name} ${s.first_name}` }))} />
      <Button size="small" type="primary" onClick={() => approve("APPROUVE", rep)}>Approuver</Button>
      <Popconfirm title="Refuser ?" onConfirm={() => approve("REFUSE")}>
        <Button size="small" danger>Refuser</Button>
      </Popconfirm>
    </Space>
  );
};

export default PersonnelPage;
